import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, Send, Phone, Loader2 } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { BUSINESS, SERVICE_OPTIONS } from "../../lib/constants";
import { makeClientId, compressPhoto, withTimeout } from "../../lib/quoteSubmit";
import { devLog } from "../../lib/devLog";
import { BookingPicker } from "./BookingPicker";
import { QuoteSuccess } from "./QuoteSuccess";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONSENT_TEXT =
  "By submitting this form, you agree to receive text messages from B&C Junk Removal Pros at the number provided regarding estimates, scheduling, appointment reminders, and service updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.";

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    description: "",
  });
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [preferredDate, setPreferredDate] = useState(null);
  const [preferredWindow, setPreferredWindow] = useState("");
  const formSnapshotRef = useRef(null);
  const clientIdRef = useRef(makeClientId());

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const captureScreenshot = async () => {
    try {
      if (!formSnapshotRef.current) return "";
      const canvas = await html2canvas(formSnapshotRef.current, {
        backgroundColor: "#ffffff",
        scale: 1,
        useCORS: true,
        logging: false,
      });
      return canvas.toDataURL("image/jpeg", 0.5);
    } catch (err) {
      devLog("Screenshot capture failed:", err);
      return "";
    }
  };

  const handleSuccess = () => {
    setSubmitted(true);
    toast.success("Quote request sent — we'll be in touch fast!");
    setForm({ name: "", phone: "", email: "", service: "", description: "" });
    setPhoto(null);
    setConsent(false);
    setPreferredDate(null);
    setPreferredWindow("");
    clientIdRef.current = makeClientId();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in name, phone, email, and service.");
      return;
    }
    if (!consent) {
      toast.error("Please check the SMS consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const screenshot = await withTimeout(captureScreenshot(), 8000, "");

      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("service", form.service);
      fd.append("description", form.description || "");
      if (photo) {
        const compressed = await withTimeout(compressPhoto(photo), 10000, photo);
        fd.append("photo", compressed);
      }
      if (preferredDate)
        fd.append("preferred_date", format(preferredDate, "yyyy-MM-dd"));
      if (preferredWindow) fd.append("preferred_window", preferredWindow);
      fd.append("sms_consent", "true");
      fd.append("consent_text", CONSENT_TEXT);
      if (screenshot) fd.append("consent_screenshot", screenshot);
      fd.append("client_id", clientIdRef.current);

      await axios.post(`${API}/quotes`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      handleSuccess();
    } catch (err) {
      devLog("Quote submit failed:", err);
      // The request may have gone through even if the response was lost
      // (slow connection / timeout). Verify before showing an error.
      try {
        const check = await axios.get(
          `${API}/quotes/verify/${clientIdRef.current}`,
          { timeout: 15000 }
        );
        if (check.data?.exists) {
          handleSuccess();
          return;
        }
      } catch (verifyErr) {
        devLog("Submission verification unavailable:", verifyErr);
      }
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please call us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="bg-offwhite py-24 sm:py-32 border-b-2 border-navy"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <span className="label-pill">
              <span className="h-1.5 w-1.5 bg-gold" />
              06 — Get a free quote
            </span>
            <h2 className="font-display uppercase text-navy text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
              Tell us about <br /> the <span className="text-gold">job.</span>
            </h2>
            <p className="mt-6 font-body text-navy/75 text-base sm:text-lg max-w-md">
              Fill out the form and we'll respond fast with a flat price. Prefer
              to talk? Hit the call button — we're open 8am to 8pm on weekends.
            </p>

            <div className="mt-8 border-2 border-navy bg-navy text-white">
              <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                <span className="font-display uppercase text-sm tracking-wider">
                  {BUSINESS.hours}
                </span>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="quote-side-call-btn"
                  className="inline-flex items-center gap-2 text-gold font-display uppercase text-sm"
                >
                  <Phone className="h-4 w-4" strokeWidth={3} />
                  {BUSINESS.phoneDisplay}
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <motion.form
            data-testid="quote-form"
            ref={formSnapshotRef}
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7 bg-white border-2 border-navy p-6 sm:p-10 space-y-5"
          >
            {submitted ? (
              <QuoteSuccess onReset={() => setSubmitted(false)} />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Your Name" required>
                    <input
                      data-testid="quote-name-input"
                      required
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      data-testid="quote-phone-input"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(315) 000-0000"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="Email" required>
                  <input
                    data-testid="quote-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="form-input"
                  />
                </Field>

                <Field label="Service Needed" required>
                  <select
                    data-testid="quote-service-select"
                    required
                    value={form.service}
                    onChange={update("service")}
                    className="form-input"
                  >
                    <option value="">Select a service…</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Job Description">
                  <textarea
                    data-testid="quote-description-input"
                    rows={4}
                    value={form.description}
                    onChange={update("description")}
                    placeholder="Tell us what needs to go — items, rough volume, access, timeline…"
                    className="form-input resize-none"
                  />
                </Field>

                <Field label="Photo Upload (optional)">
                  <label
                    htmlFor="quote-photo"
                    className="flex items-center gap-3 border-2 border-dashed border-navy/60 px-4 py-4 cursor-pointer hover:bg-offwhite transition-colors"
                  >
                    <Upload
                      className="h-5 w-5 text-navy shrink-0"
                      strokeWidth={2.5}
                    />
                    <span className="font-body text-sm text-navy truncate">
                      {photo ? photo.name : "Attach a photo of the job"}
                    </span>
                  </label>
                  <input
                    id="quote-photo"
                    data-testid="quote-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </Field>

                <BookingPicker
                  preferredDate={preferredDate}
                  onDateChange={setPreferredDate}
                  preferredWindow={preferredWindow}
                  onWindowChange={setPreferredWindow}
                />

                {/* SMS Consent */}
                <div className="pt-2 border-t border-navy/15">
                  <label
                    className="flex items-start gap-3 cursor-pointer group"
                    data-testid="sms-consent-label"
                  >
                    <input
                      data-testid="sms-consent-checkbox"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                      className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#f5a623] border-2 border-navy"
                    />
                    <span
                      data-testid="sms-consent-text"
                      className="text-[11px] leading-relaxed text-navy/75"
                    >
                      {CONSENT_TEXT}
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  data-testid="quote-submit-btn"
                  disabled={submitting}
                  className="brutal-btn w-full inline-flex items-center justify-center gap-3 bg-navy text-white px-6 py-5 font-display text-base uppercase tracking-wider border-2 border-navy disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" strokeWidth={3} />
                      Get My Free Quote
                    </>
                  )}
                </button>

                <p className="text-[10px] text-navy/50 text-center leading-relaxed">
                  We log timestamp, phone, IP address and a snapshot of this
                  form as proof of your consent, per TCPA guidelines.
                </p>
              </>
            )}
          </motion.form>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.9rem 1rem;
          background: #fff;
          border: 2px solid #0a192f;
          border-radius: 0;
          font-family: 'Manrope', sans-serif;
          font-size: 1rem;
          color: #0a192f;
          outline: none;
          transition: box-shadow .15s ease, transform .15s ease;
        }
        .form-input:focus {
          box-shadow: 4px 4px 0 0 #f5a623;
          transform: translate(-2px,-2px);
        }
        .form-input::placeholder { color: #94a3b8; }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block font-body text-xs font-bold uppercase tracking-[0.18em] text-navy mb-2">
      {label} {required && <span className="text-gold">*</span>}
    </span>
    {children}
  </label>
);
